// The shell the three grip-reference pages share (Chord Library, Scale
// Harmony, Progressions).
//
// All three do ONE job — pick parameters in a control bar, then read a grid of
// diagrams — but each had grown its own version of every part: Library's
// control bar carried an inline `width: fit-content` hack, Scale Harmony a
// `.scale-harmony-controls` override, Progressions nothing; only Library
// opted into the display-scale plumbing, so the same grip drew at 92px on one
// page and 150px on another. One shell so a page cannot drift again by
// forgetting a wrapper.
//
// The `data-chart-scale` attribute is the load-bearing bit: index.css keys the
// `--diagram-max-size` maths off it (see displayScale.ts), so setting it here
// means EVERY reference page's diagrams answer to the one persisted
// `chartScale` — the thing Library had and the other two silently lacked.

import type { ReactNode } from 'react';
import { scaleVars } from '@/state/displayScale';
import { ScaleSlider } from '@/components/ScaleSlider';
import { useStore } from '@/state/store';

interface ReferencePageProps {
  /** Page title. These pages carry a heading and no blurb — the controls and
   *  diagrams below say what the page is faster than a paragraph does. */
  title: string;
  /** Extra class on the root, for page-specific grid rules. */
  className?: string;
  /** The control-bar contents: `.control-group` children. The Chord size
   *  slider is appended by the shell, so no page hand-rolls its own. */
  controls: ReactNode;
  children: ReactNode;
}

export function ReferencePage({ title, className, controls, children }: ReferencePageProps) {
  // Deliberately the SAME scale as the workbench's "Chord size" (see
  // displayScale.ts): grips are chart-surface content, and one setting must
  // size them everywhere — a second per-page zoom is how the old S/M/L tier
  // mess started.
  const chartScale = useStore((s) => s.chartScale);
  const setChartScale = useStore((s) => s.setChartScale);

  return (
    <div
      className={className ? `reference-page ${className}` : 'reference-page'}
      data-chart-scale=""
      style={scaleVars({ chart: chartScale })}
    >
      <div className="page-header">
        <h1>{title}</h1>
      </div>

      <div className="control-bar reference-controls">
        {controls}
        <div className="control-group">
          <span className="label">Chord size</span>
          <ScaleSlider
            value={chartScale}
            onChange={setChartScale}
            label="Chord size"
            testid="reference-chart-scale"
          />
        </div>
      </div>

      {children}
    </div>
  );
}
